/**
 * Woerter fuer das Radar — Enums aus der API in Deutsch fuer die Oberflaeche.
 *
 * Die Schluessel entsprechen exakt den Werten in `schemas/radar.py`. Wer dort
 * einen Wert ergaenzt, ergaenzt ihn hier.
 */

import { formatDatum, statusInfo } from '@elbklar/ui';

import type {
  RadarActorType,
  RadarDeadlineKind,
  RadarEffort,
  RadarKind,
  RadarRewardKind,
  RadarSort,
  RadarSourceState,
  RadarStatus,
} from '@/types';

export const KIND_TEXT: Record<RadarKind, string> = {
  foerderung: 'Förderung',
  wettbewerb: 'Wettbewerb',
  // „Events“ heißt in der Oberfläche „Veranstaltungen“ (U5, Auftrag 010). Der
  // Wert `event` aus der API bleibt.
  event: 'Veranstaltung',
};

export const KIND_PLURAL: Record<RadarKind, string> = {
  foerderung: 'Förderungen',
  wettbewerb: 'Wettbewerbe',
  event: 'Veranstaltungen',
};

/*
 * Wörter und Töne der Status stehen seit D2 in `STATUS` von @elbklar/ui
 * (`radar_…`, `akteur_…`, `lauf_…`, `quelle_…`, Tabelle „Status-Erweiterungen“
 * im Plan zu Auftrag 010). Hier bleibt nur die Reihenfolge für Auswahlen.
 */

/** Text eines Radar-Status, aus `STATUS` (`radar_…`). */
export function statusText(status: RadarStatus): string {
  return statusInfo(`radar_${status}`).label;
}

export const STATUS_REIHENFOLGE: RadarStatus[] = [
  'neu',
  'geprueft',
  'beobachten',
  'in_bearbeitung',
  'beantragt',
  'bewilligt',
  'abgelehnt',
  'verworfen',
  'abgelaufen',
];

/** Ebene eines Programms (`level`, Werte aus `radar_discovery.py` und den Seeds). */
const LEVEL_TEXT: Record<string, string> = {
  eu: 'EU',
  bund: 'Bund',
  land: 'Land',
  kommune: 'Kommune',
};

/** Ebene als Wort; Unbekanntes (von Hand eingetragen) als Rohwert. */
export function ebeneText(level: string | null): string | null {
  return level ? (LEVEL_TEXT[level] ?? level) : null;
}

export const DEADLINE_KIND_TEXT: Record<RadarDeadlineKind, string> = {
  fest: 'feste Frist',
  laufend: 'laufend',
  aufruf: 'Aufruf',
  unbekannt: 'unbekannt',
};

export const SOURCE_STATE_HINWEIS: Record<RadarSourceState, string> = {
  ungeprueft: 'Die Quelle wurde noch nicht abgerufen.',
  lesbar: 'Die Quelle wird automatisch überwacht.',
  umgezogen: 'Die Seite leitet woandershin — Adresse prüfen.',
  unsicher: 'Der Abruf lieferte etwas, aber nicht sicher die richtige Seite.',
  js: 'Die Seite lädt ihren Inhalt per Script — der einfache Abruf sieht nichts.',
  gesperrt: 'Die Seite blockt automatische Abrufe — händisch prüfen.',
  quelle_tot: 'Die Adresse antwortet nicht mehr — händisch prüfen.',
  nicht_lesbar: 'Der Inhalt ließ sich nicht auswerten — händisch prüfen.',
};

export const REWARD_KIND_TEXT: Record<RadarRewardKind, string> = {
  geld: 'Preisgeld',
  media: 'Sichtbarkeit',
  beteiligung: 'Beteiligung',
  pilot: 'Pilotprojekt',
  programm: 'Programmplatz',
  sachleistung: 'Sachleistung',
};

export const EFFORT_TEXT: Record<RadarEffort, string> = {
  niedrig: 'niedrig',
  mittel: 'mittel',
  hoch: 'hoch',
};

export const ACTOR_TYPE_TEXT: Record<RadarActorType, string> = {
  foerderbank: 'Förderbank',
  landesagentur: 'Landesagentur',
  ministerium: 'Ministerium',
  projekttraeger: 'Projektträger',
  kammer: 'Kammer',
  netzwerk: 'Netzwerk',
  verband: 'Verband',
  cluster: 'Cluster',
  hochschule: 'Hochschule',
  stiftung: 'Stiftung',
  corporate: 'Unternehmen',
  messeveranstalter: 'Messeveranstalter',
  behoerde: 'Behörde',
};

export const SORT_TEXT: Record<RadarSort, string> = {
  deadline: 'Frist',
  score: 'Score',
  title: 'Titel',
  updated: 'Zuletzt geändert',
};

export const FIELD_TEXT: Record<string, string> = {
  title: 'Titel',
  provider: 'Träger',
  summary: 'Zusammenfassung',
  target_group: 'Zielgruppe',
  amount_min: 'Betrag min.',
  amount_max: 'Betrag max.',
  funding_rate: 'Förderquote',
  reward_kind: 'Gewinnart',
  application_path: 'Antragsweg',
  prerequisites: 'Voraussetzungen',
  combinable_with: 'Kombinierbar mit',
  deadline: 'Frist',
  deadline_kind: 'Fristart',
  event_date: 'Datum',
  event_end_date: 'Ende',
  location: 'Ort',
  score: 'Score',
  status: 'Status',
  source_url: 'Quelle',
  // Aus der Bewertung einer Änderung (`field_changes`, services/radar_monitor.py).
  program_state: 'Programmstand',
  confidence: 'Sicherheit',
};

/** Werte aus `field_changes`, die der Roboter als Schlüssel schreibt (`radar_assess.py`). */
const WERT_TEXT: Record<string, string> = {
  antragsstopp: 'Antragsstopp',
};

/** Optionen fuer ein Select aus einer Text-Tabelle. */
export function optionen<K extends string>(
  texte: Record<K, string>,
  reihenfolge?: K[]
): { value: K; label: string }[] {
  const schluessel = reihenfolge ?? (Object.keys(texte) as K[]);
  return schluessel.map((value) => ({ value, label: texte[value] }));
}

/** Schluessel aus `details` lesbar: "antrags_frist" -> "Antrags frist". */
export function schluesselText(schluessel: string): string {
  const bekannt = FIELD_TEXT[schluessel];
  if (bekannt) return bekannt;
  const wort = schluessel.replace(/_/g, ' ');
  return wort.charAt(0).toUpperCase() + wort.slice(1);
}

/** Beliebiger Wert aus `details` als Text. */
export function wertText(wert: unknown): string {
  if (wert === null || wert === undefined || wert === '') return '–';
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein';
  if (Array.isArray(wert)) return wert.map(wertText).join(', ');
  if (typeof wert === 'object') return JSON.stringify(wert);
  if (typeof wert === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(wert)) return formatDatum(`${wert}T00:00:00`);
  return typeof wert === 'string' ? (WERT_TEXT[wert] ?? wert) : String(wert);
}

/**
 * Eine Feldänderung aus `field_changes`: `[alt, neu]` als „alt → neu“, alles
 * andere wie `wertText`.
 */
export function aenderungText(wert: unknown): string {
  if (Array.isArray(wert) && wert.length === 2) return `${wertText(wert[0])} → ${wertText(wert[1])}`;
  return wertText(wert);
}
